<template>
  <div v-if="column" :key="columnKey">
    <div v-if="hasBlock">
      <menu-content-container :padding-left-right="8">
        <content-row>
          <color-picker
            data-testid="column-background-color"
            :label="$t('forms.column_background_color')"
            position="left"
            :default-color="color.model"
            :changed="color.changed"
            :reset-tooltip="$t('forms.reset_value')"
            @color-change="color.model = $event"
            @reset="color.reset"
          ></color-picker>
          <t-input-number
            v-model="borderRadius.model"
            data-testid="column-border-radius"
            :label="$t('forms.button_border')"
            :min="0"
            :max="99"
            unit=""
            :changed="borderRadius.changed"
            :reset-tooltip="$t('forms.reset_value')"
            @reset="borderRadius.reset"
          ></t-input-number>
        </content-row>
        <content-row class="!block" data-testid="column-gradient-form">
          <TButton
            v-if="!gradient.model"
            data-testid="column-add-gradient"
            @click="
              gradient.model =
                'linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)'
            "
          >
            <template #icon>
              <svg
                width="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19.958 3C20.3669 3 20.7597 3.16205 21.0488 3.45117C21.3379 3.74029 21.5 4.13312 21.5 4.54199V19.958C21.5 20.3669 21.3379 20.7597 21.0488 21.0488C20.7597 21.3379 20.3669 21.5 19.958 21.5H4.54199C4.13312 21.5 3.74029 21.3379 3.45117 21.0488C3.16205 20.7597 3 20.3669 3 19.958V4.54199C3 4.13312 3.16205 3.74029 3.45117 3.45117C3.74029 3.16205 4.13312 3 4.54199 3H19.958ZM10.708 4.54199V7.625H7.625V10.708H10.708V13.792H7.625V16.875H10.708V19.958H13.792V16.875H16.875V19.958H19.958V4.54199H16.875V7.625H13.792V4.54199H10.708ZM13.792 16.875H10.708V13.792H13.792V16.875ZM16.875 13.792H13.792V10.708H16.875V13.792ZM13.792 10.708H10.708V7.625H13.792V10.708Z"
                  fill="currentColor"
                />
              </svg>
            </template>
            {{ $t("forms.add_gradient") }}
          </TButton>
          <TTabsToggle
            v-if="gradient.model"
            :header="$t('forms.gradient')"
            :changed="gradient.changed"
            default-open
            no-move
            show-remove
            can-be-reset
            @remove-block="gradient.model = undefined"
            @reset="gradient.reset"
          >
            <menu-content-container border-full :padding-left-right="8">
              <TGradient v-model="gradient.model" />
            </menu-content-container>
          </TTabsToggle>
        </content-row>

        <content-row :margin-bottom="16">
          <t-note>{{ $t("layout.background_gradient_text") }}</t-note>
        </content-row>
      </menu-content-container>
      <padding-form
        data-testid="column-padding-form"
        :label="$t('forms.column_margin')"
        :external-padding="columnPadding.model"
        :external-usage="true"
        :padding-left-right="8"
        :margin-bottom="8"
        :border-bottom="false"
        :changed="columnPadding.changed"
        :default-value="columnPadding.defaultValue"
        @paddingChanged="columnPadding.model = $event"
        @reset="columnPadding.reset"
      ></padding-form>

      <menu-content-container border-full :padding-left-right="8">
        <content-row>
          <TSwitch
            v-model="innerAttributes"
            data-testid="column-inner-attributes"
            :label="$t('forms.column_inner_margin')"
            :changed="innerAttrBinding.changed"
            @reset="innerAttrBinding.reset"
          />
        </content-row>
      </menu-content-container>

      <border-form
        data-testid="column-border-form"
        color-picker-top
        :header-title="$t('forms.column_border')"
        :border="border.model"
        :default-border="border.defaultValue"
        :default-border-top="borderTop.defaultValue"
        :default-border-right="borderRight.defaultValue"
        :default-border-bottom="borderBottom.defaultValue"
        :default-border-left="borderLeft.defaultValue"
        :border-top="borderTop.model"
        :border-bottom="borderBottom.model"
        :border-left="borderLeft.model"
        :border-right="borderRight.model"
        :padding-left-right="8"
        :changed="borderAnyChanged"
        :changed-top="borderTop.changed"
        :changed-right="borderRight.changed"
        :changed-bottom="borderBottom.changed"
        :changed-left="borderLeft.changed"
        @borderChanged="onBorderChanged"
        @reset="onBorderReset"
        @reset-top="borderTop.reset"
        @reset-right="borderRight.reset"
        @reset-bottom="borderBottom.reset"
        @reset-left="borderLeft.reset"
      >
      </border-form>
    </div>
    <menu-content-container
      v-else
      :border-bottom="false"
      :padding-left-right="8"
    >
      <content-row class="!mb-2">
        <t-note>{{ $t("forms.no_content") }}.</t-note>
      </content-row>
    </menu-content-container>
  </div>
</template>

<script lang="ts" setup>
  import colorPicker from "./colorPicker.vue";
  import paddingForm from "../paddingForm.vue";
  import BorderForm from "../borderForm.vue";
  import TGradient from "@/components/TGradient/TGradient.vue";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useBlockAttr } from "@/composables/useBlockAttr";
  import {
    TInputNumber,
    TNote,
    TSwitch,
    TButton,
    TTabsToggle,
  } from "@topol/twig-ui";
  import { computed, watch } from "vue";

  const props = defineProps({
    columnIndex: {
      type: Number,
      required: true,
    },
    blockColumn: {
      type: Boolean,
      default: false,
    },
  });

  const mjmlStore = useMjmlStore();

  const { nullable, px, anyPropChanged, resetAttr } = useBlockAttr({
    mode: "column",
    columnIndex: props.columnIndex,
    blockColumn: props.blockColumn,
  });

  // --- Composable-backed bindings ---

  const color = nullable("background-color");
  const gradient = nullable("column-gradient");
  const borderRadius = px("border-radius", 0);
  const columnPadding = nullable("padding");
  const innerAttrBinding = nullable("inner-attributes");
  const border = nullable("border");
  const borderTop = nullable("border-top");
  const borderBottom = nullable("border-bottom");
  const borderLeft = nullable("border-left");
  const borderRight = nullable("border-right");
  const borderAnyChanged = anyPropChanged(
    "border",
    "border-top",
    "border-bottom",
    "border-left",
    "border-right"
  );

  // Side effect: manage css-class based on border-radius value
  watch(
    () => borderRadius.model,
    (val) => {
      const updateFn = props.blockColumn
        ? mjmlStore.updateSpecificBlockColumnAttribute
        : mjmlStore.updateSpecificColumnAttribute;
      if (val > 0) {
        if (column.value?.attributes?.["css-class"] !== "rounded") {
          updateFn({
            columnIndex: props.columnIndex,
            prop: "css-class",
            value: "rounded",
          });
        }
      } else if (column.value?.attributes?.["css-class"] === "rounded") {
        updateFn({
          columnIndex: props.columnIndex,
          prop: "css-class",
          value: null,
        });
      }
    }
  );

  const sectionIndex = computed(() => {
    if (props.blockColumn && mjmlStore.getActiveBlock) {
      return mjmlStore.getActiveBlock.uid;
    }
    return mjmlStore.getSelectedSectionByIndex.uid;
  });

  const columnKey = computed(() => {
    return `key-${sectionIndex.value}-${props.columnIndex}`;
  });
  const activeBlock = computed(() => {
    if (props.blockColumn) {
      return mjmlStore.getActiveBlock;
    }
    return null;
  });

  const column = computed(() => {
    if (props.blockColumn) {
      const activeBlock = mjmlStore.getActiveBlock;
      if (
        !activeBlock ||
        !mjmlStore.tagsWithColumnChildren.includes(activeBlock.tagName) ||
        !("children" in activeBlock)
      ) {
        return null;
      }
      return activeBlock.children[props.columnIndex];
    }
    try {
      const section = mjmlStore.getSelectedSectionByIndex();
      return section.children[props.columnIndex];
    } catch (err) {
      // wierd bug that section is undefined here which makes no sense
      return null;
    }
  });

  const hasBlock = computed(() => {
    if (activeBlock.value && activeBlock.value.tagName === "mj-feed-block") {
      return true;
    }
    return column.value.children.length > 0;
  });

  const innerAttributes = computed({
    get() {
      return !innerAttrBinding.model;
    },
    set(val: boolean) {
      innerAttrBinding.model = !val;
    },
  });

  function onBorderChanged({
    border: newBorder,
    borderType = "border",
  }: {
    border: string;
    borderType?: string;
  }) {
    if (borderType !== "border" && border.model !== undefined) {
      border.model = undefined;
    }

    if (newBorder === undefined || newBorder.startsWith("0px")) {
      resetAttr(borderType);
    } else {
      const updateFn = props.blockColumn
        ? mjmlStore.updateSpecificBlockColumnAttribute
        : mjmlStore.updateSpecificColumnAttribute;
      updateFn({
        columnIndex: props.columnIndex,
        prop: borderType,
        value: newBorder,
      });
    }
  }

  function onBorderReset() {
    for (const prop of [
      "border",
      "border-top",
      "border-bottom",
      "border-left",
      "border-right",
    ]) {
      resetAttr(prop);
    }
  }
</script>

<style></style>
